import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '@/lib/logger';

interface Props {
  children: ReactNode;
  /** Optional fallback UI; defaults to a sovereign-archive themed card. */
  fallback?: (reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary. Catches render-time exceptions below this
 * point in the tree, ships them to `clientErrors/`, and shows a minimal
 * "something went wrong" card with the trace id (support asks users to
 * paste it when filing a bug).
 *
 * Placed high in the tree (outside RouterProvider) so router errors
 * surface cleanly — but inside AuthProvider so we still know which
 * user triggered it.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error('render.boundary', error, {
      componentStack: info.componentStack ?? undefined,
    });
  }

  reset = () => this.setState({ error: null });

  override render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset);
    const traceId =
      typeof window !== 'undefined' &&
      (window as unknown as { __jamTraceId?: string }).__jamTraceId;

    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-md space-y-4 rounded-sm border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
          <div className="text-xxs uppercase tracking-wider text-[var(--color-critical)]">
            Something broke.
          </div>
          <h1 className="font-serif text-2xl leading-tight">Render error</h1>
          <p className="text-sm text-[var(--color-ink-secondary)]">
            An unexpected error escaped to the root of the app. Support has
            been notified. Include the trace id below when filing a bug.
          </p>
          <pre className="max-h-40 overflow-auto rounded-xs border border-[var(--color-border)] bg-[var(--color-elevated)] p-2 font-mono text-xxs text-[var(--color-ink-muted)]">
            {this.state.error.message}
          </pre>
          {traceId && (
            <div className="text-xxs text-[var(--color-ink-muted)]">
              Trace id:{' '}
              <code className="rounded-xs bg-[var(--color-elevated)] px-1 font-mono text-[var(--color-ink)]">
                {traceId}
              </code>
            </div>
          )}
          <div className="flex justify-end">
            <button
              onClick={this.reset}
              className="rounded-sm bg-[var(--color-ink)] px-4 py-2 text-sm text-[var(--color-canvas)] hover:bg-[var(--color-accent)]"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
